import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as api from '../../../../features/scenarios/api';
import type { GenerateStoryboardsOptions, GenerateStoryboardsResult } from '../../../../features/scenarios/api';
import { useGenerationState } from '../../state/useGenerationState';
import type { ActiveJob } from '../../state/useGenerationState';
import { useToast } from '../../../../hooks/useToast';
import { waitForJobCompletion } from '../../../../services/asyncJobApi';
import { fetchBrandsByAsins } from '../../hooks/useBrandLogos';
import { backfillMissingLogoSheets } from '../../../../features/brands/logoSheet';
import type { Scenario } from '../types';

// ストーリーボード(絵コンテ)静止画の生成モデル。StoryboardCandidates と同じ規約で、
// 全動画モデル共通で Gemini 標準 (nano-banana 系) に統一 (2026-08-14・線画絵コンテ
// フォーマットへの全面移行に伴う。1K 固定・高速・低ポイント)。2026-10-08: Nano Banana 2.1 に切替。
const STORYBOARD_MODEL = 'gemini-nano-banana-2.1';

interface RunStoryboardJobParams {
  scenarioId: string;
  scenarioTitle?: string;
  // 次に生成されるストーリーボードの letter index (0-based)。既存の最大 letter_index + 1。
  baseLetterIndex?: number;
  options?: GenerateStoryboardsOptions;
  // 絵コンテ自動生成の集約通知バッチ ID。指定時はこのジョブの個別完了通知を抑止し、
  // バッチのスロットとして数える (投入失敗/完了/失敗のいずれでも resolve する)。
  batchId?: string;
}

/**
 * ストーリーボード生成ジョブを 1 本起動し、activeJobs に紐づけて完了まで追跡する低レベル関数を返す。
 *
 * ポーリングは「シナリオ単位の activeJobs（GenerationContext）」に紐づけて detached
 * promise で回すため、別シナリオへ遷移してコンポーネントが unmount されても生成は
 * 途切れず、戻ってきたときに進捗・結果が復元される。手動生成 (useStoryboardGeneration)
 * と自動生成 (useAutoStoryboardGeneration) の両方からこの runner を共有する。
 *
 * setActiveJobs・queryClient・toast はいずれも上位 Provider 由来で安定参照のため、
 * detached promise から unmount 後に呼んでも問題ない。
 */
export function useStoryboardJobRunner() {
  const { setActiveJobs, resolveStoryboardBatchSlot } = useGenerationState();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useCallback(
    ({ scenarioId, scenarioTitle, baseLetterIndex = 0, options, batchId }: RunStoryboardJobParams) => {
      void (async () => {
        let jobId: string;
        try {
          const res = await api.createStoryboardJob(scenarioId, options);
          jobId = res.job_id;
        } catch (err) {
          // ジョブ投入自体に失敗 → activeJobs には載らないので、バッチのスロットを
          // ここで確定 (resolve) しないと「全絵コンテ完了」に到達しなくなる。
          if (batchId) resolveStoryboardBatchSlot(batchId);
          toast.error((err as Error)?.message || 'ストーリーボード生成の開始に失敗しました');
          return;
        }

        setActiveJobs((prev) => {
          // ストーリーボードの通し letter (A, B, ...)。既存の最大 letter_index + 1 を起点に、
          // キュー内で進行中の同シナリオジョブ数だけオフセットする。同時生成でも重複しない。
          const inFlight = prev.filter((j) => j.type === 'storyboard' && j.scenarioId === scenarioId).length;
          const letter = String.fromCharCode(65 + baseLetterIndex + inFlight);
          const label = scenarioTitle ? `${scenarioTitle} - ${letter}` : `ストーリーボード ${letter}`;
          return [
            ...prev,
            {
              jobId,
              status: 'polling' as const,
              startedAt: Date.now(),
              type: 'storyboard' as const,
              scenarioId,
              label,
              // バッチ所属時は個別通知せず、集約通知に回す
              ...(batchId ? { notifyBatchId: batchId } : {}),
            },
          ];
        });

        try {
          // 絵コンテ生成は参照画像多数 + 複数枚で数分かかる。バックエンドの
          // OpenAI タイムアウト (最大 ~20分) を吸収できるよう余裕を持って待つ。
          const job = await waitForJobCompletion<GenerateStoryboardsResult>(jobId, {
            maxDuration: 22 * 60 * 1000,
            // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
            skipVisibilityCheck: true,
          });
          const first = job.result_data?.storyboards?.[0];
          setActiveJobs((prev) =>
            prev.map((j) =>
              j.jobId === jobId
                ? {
                    ...j,
                    status: 'completed' as const,
                    result: first
                      ? {
                          id: first.id,
                          material_id: first.material_id ?? undefined,
                          image_url: first.thumbnail_url ?? first.image_url ?? undefined,
                        }
                      : { id: jobId },
                  }
                : j,
            ),
          );
          // 別シナリオを見ていても、戻ってきたときに最新が反映されるよう
          // 該当シナリオのクエリを無効化する。
          queryClient.invalidateQueries({ queryKey: ['storyboards', scenarioId] });
          queryClient.invalidateQueries({ queryKey: ['scenarios', scenarioId] });
          // バッチのスロットを完了として確定する。
          if (batchId) resolveStoryboardBatchSlot(batchId);
        } catch (err) {
          const msg = (err as Error)?.message || 'ストーリーボード生成に失敗しました';
          setActiveJobs((prev) =>
            prev.map((j) => (j.jobId === jobId ? { ...j, status: 'failed' as const, error: msg } : j)),
          );
          // 失敗もバッチのスロット確定 (成功/失敗問わず全確定で通知する仕様)。
          if (batchId) resolveStoryboardBatchSlot(batchId);
          toast.error(msg);
        }
      })();
    },
    [setActiveJobs, queryClient, toast, resolveStoryboardBatchSlot],
  );
}

/**
 * ストーリーボード（絵コンテ）静止画生成をジョブとして起動し、完了まで追跡するフック。
 *
 * - `isGenerating` / `error` は activeJobs から導出するのでコンポーネントの再マウントに耐える
 * - 完了時に該当シナリオの storyboards / scenarios クエリを invalidate する (runner 内)
 */
export function useStoryboardGeneration(
  scenarioId: string,
  scenarioTitle?: string,
  // 次に生成されるストーリーボードの letter index (0-based)。既存の最大 letter_index + 1。
  // 0→A, 1→B, ... と表示する (StoryboardCandidates のカード letter と同一規約)。
  baseLetterIndex = 0,
) {
  const { activeJobs } = useGenerationState();
  const runStoryboardJob = useStoryboardJobRunner();

  // この scenario の最新ストーリーボードジョブ（startedAt が最大のもの）。
  // 再生成時は新ジョブが最新になるため、古い失敗ジョブのエラーは引きずらない。
  const latestJob = activeJobs.reduce<ActiveJob | null>((acc, j) => {
    if (j.type !== 'storyboard' || j.scenarioId !== scenarioId) return acc;
    return acc == null || j.startedAt > acc.startedAt ? j : acc;
  }, null);

  const isGenerating = latestJob?.status === 'polling';
  const error = latestJob?.status === 'failed' ? (latestJob.error ?? 'ストーリーボードの生成に失敗しました') : null;

  const generate = useCallback(
    (options?: GenerateStoryboardsOptions) => {
      runStoryboardJob({ scenarioId, scenarioTitle, baseLetterIndex, options });
    },
    [runStoryboardJob, scenarioId, scenarioTitle, baseLetterIndex],
  );

  return { generate, isGenerating, error };
}

/**
 * 「ストーリーボードを自動生成」チェック ON のとき、シナリオ生成完了ごとに
 * 絵コンテ静止画生成をシナリオ別キューで自動起動するためのハンドラを返す。
 *
 * 仕様 (ユーザー要件):
 *  - 1 枚生成 (count=1) / 最初のフレームのみ (frame_mode='first_only')
 *  - ロゴ・商品画像・絵コンテイラストは「可能なものはすべて On」
 *    - ロゴ: シナリオの商品にブランドロゴがあれば添付 (per-scenario 判定 + シートバックフィル)
 *    - 商品画像: 選択中の参照素材を送る。未選択なら backend が商品テーブルから自動添付
 *    - 絵コンテイラスト: 非写実系スタイル以外なら ON
 *  - すべてのシナリオ完了を待たず、シナリオ別キューで完了したものから順に起動する
 *    (各 scenario を渡された時点で個別にジョブ投入するため、ランダム生成のシェル
 *     フローでは ready になったシナリオから 1 本ずつ走り出す)
 */
export function useAutoStoryboardGeneration(params: {
  castingGenderFilter?: string;
  castingAgeFilter?: number | null;
}) {
  const { visualStyle, visualStyleIsStylized, getAssetsForScope, addStoryboardBatchSlot } = useGenerationState();
  const queryClient = useQueryClient();
  const runStoryboardJob = useStoryboardJobRunner();

  // シェルポーリング後 (数分後) に発火しても最新値を読むため ref に同期する。
  const visualStyleRef = useRef(visualStyle);
  visualStyleRef.current = visualStyle;
  const isStylizedRef = useRef(visualStyleIsStylized);
  isStylizedRef.current = visualStyleIsStylized;
  const castingRef = useRef(params);
  castingRef.current = params;
  // getAssetsForScope は context 由来で安定参照だが、念のため ref に保持する。
  const getAssetsForScopeRef = useRef(getAssetsForScope);
  getAssetsForScopeRef.current = getAssetsForScope;

  return useCallback(
    (scenarios: Scenario[], batchId?: string) => {
      scenarios.forEach((scenario) => {
        // バッチのスロット登録は async IIFE の外 (= 同期) で行う。これにより、
        // 後続の closeStoryboardBatch より前に必ず total/pending が増え、
        // 「ジョブ投入前に空判定で完了通知が誤発火する」レースを防ぐ。
        if (batchId) addStoryboardBatchSlot(batchId);
        void (async () => {
          // ── ロゴ可否を このシナリオの商品 で判定 (per-scenario) ──
          // useBrandLogos と同じクエリを imperative に叩く (フックはループ内で呼べないため)。
          const asins = scenario.asin ? [scenario.asin.toUpperCase()] : [];
          const fromCustomInput = (scenario.custom_input as Record<string, unknown> | undefined)?.product_id;
          const pid = scenario.product_id || (typeof fromCustomInput === 'string' ? fromCustomInput : null);
          const productIds = pid ? [pid] : [];

          let includeLogo = false;
          if (asins.length || productIds.length) {
            try {
              const brands = await queryClient.fetchQuery({
                queryKey: ['brand-logos', asins.slice().sort().join(','), '', productIds.slice().sort().join(',')],
                queryFn: () => fetchBrandsByAsins(asins, [], productIds),
                staleTime: 5 * 60 * 1000,
              });
              includeLogo = brands.some((b) => b.logos && b.logos.length > 0);
              if (includeLogo) {
                // ロゴ添付前に未生成シートをバックフィルする (手動フローと同じ順序)。
                // これをしないと backend がシート未生成と判断し個別ロゴ添付に落ちる。
                await backfillMissingLogoSheets(brands, queryClient);
              }
            } catch {
              includeLogo = false;
            }
          }

          const isStylized = isStylizedRef.current;
          // 参照画像は「対象シナリオのスコープ」から読む。activeScenarioId (表示中タブ)
          // に依存しないため、裏で ready になったシナリオやユーザーがタブを切り替えた後でも
          // そのシナリオ自身の素材を送れる。
          const assets = getAssetsForScopeRef.current(scenario.id);
          const selectedAssetUrls = assets
            .map((a) => (a.sourceUrl ?? '').trim())
            .filter((u) => u.length > 0);
          const selectedAssetRefs = assets
            .filter((a) => (a.sourceUrl ?? '').trim().length > 0)
            .map((a) => ({ url: a.sourceUrl!.trim(), material_id: a.id }));

          const { castingGenderFilter, castingAgeFilter } = castingRef.current;
          const castingGender =
            castingGenderFilter && castingGenderFilter !== 'none' ? castingGenderFilter : undefined;
          const castingAge = typeof castingAgeFilter === 'number' ? castingAgeFilter : undefined;

          // キュー表示の letter は既存候補数を考慮する (既存最大 letter_index + 1)。
          // 自動生成は生成直後の新規シナリオに走るため通常 0(=A) だが、キャッシュに既存候補が
          // あれば A の重複を避ける (StoryboardCandidates の nextStoryboardLetterIndex と同一規約)。
          const existingStoryboards =
            (queryClient.getQueryData(['storyboards', scenario.id]) as
              | { letter_index?: number | null }[]
              | undefined) ?? [];
          const baseLetterIndex =
            existingStoryboards.length === 0
              ? 0
              : Math.max(
                  ...existingStoryboards.map((sb, i) =>
                    typeof sb.letter_index === 'number' ? sb.letter_index : i,
                  ),
                ) + 1;

          runStoryboardJob({
            scenarioId: scenario.id,
            scenarioTitle: scenario.title,
            baseLetterIndex,
            batchId,
            options: {
              count: 1,
              model: STORYBOARD_MODEL,
              frame_mode: 'first_only',
              // 絵コンテイラストは「可能なら On」= 非写実系スタイル以外で ON。
              illustration_storyboard: !isStylized || undefined,
              visual_style: visualStyleRef.current,
              // ロゴは「可能なら On」= 商品にブランドロゴがあれば添付。
              include_logo: includeLogo ? true : undefined,
              casting_gender: castingGender,
              casting_age: castingAge,
              // 参照画像 (商品画像含む) が選択済みなら送る。未選択なら キー自体を
              // 送らず、backend に商品テーブルからの自動添付 (= 商品画像 On) を任せる。
              ...(selectedAssetUrls.length > 0
                ? { selected_asset_urls: selectedAssetUrls, selected_assets: selectedAssetRefs }
                : {}),
            },
          });
        })();
      });
    },
    [queryClient, runStoryboardJob, addStoryboardBatchSlot],
  );
}
